import type { ReactNode } from 'react';
import { Info, Triangle, User } from 'lucide-react';
import { Tooltip } from '@parrot/ui';
import { cx } from '@parrot/utils';
import { TrendSpark } from './TrendSpark';
import styles from './StatWidget.module.css';

/** Metric family — drives the sparkline colour via the --metric-* / accent tokens. */
export type StatTone = 'revenue' | 'acquired' | 'retained' | 'loss';

const TONE_COLOR: Record<StatTone, string> = {
  revenue: 'var(--accent-secondary)',
  acquired: 'var(--metric-acquired)',
  retained: 'var(--metric-retained)',
  loss: 'var(--metric-loss)',
};

export interface StatWidgetProps {
  /** "Total Streaming Revenue" */
  title: string;
  /** What the number covers: "Prime Video, Q1 2024 – Q2 2026, All Regions". */
  scope: string;
  /** Optional explanation behind the ⓘ in the header. */
  hint?: ReactNode;
  /** Muted glyph before the value: '$' for money, a person for users. */
  unit?: 'usd' | 'users';
  /** Pre-formatted value without the currency sign, e.g. "278M". '—' when absent. */
  value: string;
  /** d360's rank beside the value: "#3". */
  rank?: number;
  /** Places climbed since the prior period (positive = better); 0 shows "=". */
  rankChange?: number;
  /** Slot beside the value — a Delta, a short note. */
  aside?: ReactNode;
  /** Series for the trend line (oldest → newest). Hidden under two points. */
  trend?: number[];
  /** One label per trend point ("Q4 2024") for the hover readout. */
  trendLabels?: string[];
  /** Formats a hovered trend value ("$84M", "3.71M"). */
  formatTrend?: (value: number) => string;
  tone?: StatTone;
  className?: string;
}

/**
 * Header-band stat, after d360's profile widgets: a titled header with the
 * scope spelled out, then a darker body with the figure, an aside (rank /
 * change) and a trend line. The scope line is the point — every number on a
 * profile covers a different slice, and this is where it says which.
 */
export function StatWidget({
  title,
  scope,
  hint,
  unit,
  value,
  rank,
  rankChange,
  aside,
  trend,
  trendLabels,
  formatTrend = String,
  tone = 'revenue',
  className,
}: StatWidgetProps) {
  return (
    <section className={cx(styles.widget, className)}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h3 className={styles.title}>{title}</h3>
          <p className={styles.scope}>{scope}</p>
        </div>
        {hint && (
          <Tooltip content={hint} placement="top">
            <Info size={13} className={styles.hint} aria-label={`About ${title}`} />
          </Tooltip>
        )}
      </header>

      <div className={styles.body}>
        <div className={styles.figure}>
          {unit === 'usd' && (
            <span className={styles.unit} aria-hidden="true">
              $
            </span>
          )}
          {unit === 'users' && <User size={26} className={styles.unitIcon} aria-hidden="true" />}
          <span className={styles.value}>{value}</span>
        </div>
        {rank !== undefined && (
          <div className={styles.rank}>
            <span className={styles.rankValue}>
              <span className={styles.rankPound}>#</span>
              {rank}
            </span>
            {rankChange !== undefined && (
              <span
                className={styles.rankChange}
                data-dir={rankChange > 0 ? 'up' : rankChange < 0 ? 'down' : 'flat'}
                aria-label={
                  rankChange === 0
                    ? 'Rank unchanged'
                    : `${Math.abs(rankChange)} place${Math.abs(rankChange) === 1 ? '' : 's'} ${rankChange > 0 ? 'up' : 'down'}`
                }
              >
                {rankChange === 0 ? (
                  '='
                ) : (
                  <>
                    <Triangle size={8} className={styles.rankArrow} aria-hidden="true" />
                    {Math.abs(rankChange)}
                  </>
                )}
              </span>
            )}
          </div>
        )}
        {aside && <div className={styles.aside}>{aside}</div>}
        {trend && trend.length > 1 && (
          <div className={styles.trend}>
            <TrendSpark
              values={trend}
              labels={trendLabels ?? trend.map((_, i) => String(i + 1))}
              format={formatTrend}
              color={TONE_COLOR[tone]}
            />
          </div>
        )}
      </div>
    </section>
  );
}
